import {
  Component,
  OnInit,
  PLATFORM_ID,
  Inject,
  OnDestroy,
  AfterViewInit,
  ViewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import {
  PamlabApiServiceService,
  RouteService,
  ScreenSizeService,
} from '@/shared/services';
import { isPlatformBrowser } from '@angular/common';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { MatTableDataSource } from '@angular/material/table';
import {
  AtRiskStudentReturn,
  RiskCommentInfo,
} from '../../../../../server/types';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@/shared/components/confirm-dialog/confirm-dialog.component';
import { Papa } from 'ngx-papaparse';
import { environment } from '../../../../../environments/environment';
import { MatSelectChange } from '@angular/material/select';
import { MatPaginator } from '@angular/material/paginator';
import { FilterSelect } from '@/core/interfaces';

@Component({
  selector: 'at-risk-student-content',
  templateUrl: './at-risk-student-content.component.html',
  styleUrls: ['./at-risk-student-content.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
  standalone: false,
})
export class AtRiskStudentContentComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  mobileView: boolean = false;

  private subscription = new Subscription();
  // labels for inner table
  innerDisplayedColumns: string[] = [
    // 'id',
    'comment',
    'categories',
    'made_by',
    'createdAt',
    'action',
  ];
  riskCommentsDataSource = new MatTableDataSource<Partial<RiskCommentInfo>>([]);
  // labels for outer table
  outerDisplayedColumns = [
    'uwf_id',
    'gender',
    'cohort',
    'tier',
    'department',
    'program_cip_desc',
    'prob_apr_1st_term',
    'uwf_gpa',
    'demo_time_frame',
    'classification',
    'changed_program',
    'action',
  ];
  atRiskStudentDataSource: MatTableDataSource<AtRiskStudentReturn> =
    new MatTableDataSource<AtRiskStudentReturn>();

  // Expanded Element
  expandedElement!: AtRiskStudentReturn | null;

  //student data variable for at-risk student
  public filterSelectObj: FilterSelect[] = [
    {
      name: 'COLLEGE',
      columnProp: 'college',
      options: [],
      defaultValue: ['All'],
    },
    {
      name: 'DEPARTMENT',
      columnProp: 'department',
      options: [],
      defaultValue: ['All'],
    },
    {
      name: 'PROGRAM',
      columnProp: 'program_cip_desc',
      options: [],
      defaultValue: ['All'],
    },
  ];
  filterDictionary = new Map<string, string>();

  constructor(
    private _apiService: PamlabApiServiceService,
    private _routerService: RouteService,
    private _routeService: RouteService,
    private _dialog: MatDialog,
    private _papa: Papa,
    private screenSizeService: ScreenSizeService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.screenSizeService.screenView$.subscribe((view) => {
        this.mobileView = view === 'mobile';
      }),
    );
  }

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngAfterViewInit(): void {
    this.atRiskStudentDataSource.paginator = this.paginator;
    this.atRiskStudentDataSource.filterPredicate = this.createFilter();

    this.subscription.add(
      this._apiService.getAtRiskStudents().subscribe({
        next: (data) => {
          this.atRiskStudentDataSource.data = data;
          this.filterSelectObj.forEach((obj: FilterSelect) => {
            obj.options = [
              'All',
              ...new Set(data.map((x: any) => x[obj.columnProp])),
            ];
          });
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 401) {
            this._routerService.navigate(environment['login_url']);
          }
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // function to redirect user to another page to view at-risk student
  viewStudent(student: any) {
    const queryParams = {
      student_id: student.uwf_id,
      year: student.cohort,
      semester: student.term,
    };
    this._routeService.navigateNewTab('/student-profile', queryParams);
  }

  // function to delete students
  deleteAtRiskStudent(atRiskStudent: AtRiskStudentReturn) {
    const dialogRef = this._dialog.open(ConfirmDialogComponent, {
      data: {
        prompt: 'Are you sure the delete this at-risk record?',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this._apiService.deleteAtRiskStudent(atRiskStudent.id).subscribe({
          next: (_res) => {
            this.updateTable(atRiskStudent);
          },
        });
      }
    });
  }

  // delete comment function
  deleteComment(commentId: number) {
    const dialogRef = this._dialog.open(ConfirmDialogComponent, {
      data: {
        prompt: 'Are you sure to delete this comment?',
      },
    });

    this.subscription.add(
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this._apiService.deleteAtRiskStudentComment(commentId).subscribe({
            next: (_result) => {
              this.updateCommentTable(commentId);
            },
            error: (_err) => {
              console.log(_err);
            },
          });
        }
      }),
    );
  }

  // function to set inner table data
  setInnerTableDataSource() {
    this.riskCommentsDataSource.data = [];
    if (this.expandedElement) {
      const innerTableData: Partial<RiskCommentInfo>[] = [];
      for (const [
        _key,
        { id, made_by, comment, categories, createdAt, updatedAt },
      ] of Object.entries(this.expandedElement.risk_comments!)) {
        innerTableData.push({
          id: id,
          made_by,
          categories,
          comment,
          createdAt: new Date(createdAt as string).toLocaleString(),
          updatedAt: new Date(updatedAt as string).toLocaleString(),
        });
      }
      this.riskCommentsDataSource.data = innerTableData;
    }
  }

  // update table after deletion of at-risk student
  updateTable(studentAtRisk: AtRiskStudentReturn) {
    const updatedTableData = this.atRiskStudentDataSource.data.filter(
      (atRiskStudent) => atRiskStudent.id !== studentAtRisk.id,
    );

    this.atRiskStudentDataSource.data = updatedTableData;
  }

  // update comment table after deletion
  updateCommentTable(id: number) {
    const updatedTableData = this.riskCommentsDataSource.data.filter(
      (comment) => comment.id !== id,
    );
    for (const row of this.atRiskStudentDataSource.data) {
      const numOfComments = row.risk_comments.length;
      row.risk_comments = row.risk_comments.filter((cmt) => cmt.id !== id);
      if (numOfComments !== row.risk_comments.length) {
        break;
      }
    }

    this.riskCommentsDataSource.data = updatedTableData;
  }

  generateRow(userInfo: AtRiskStudentReturn) {
    return {
      ...userInfo,
    };
  }

  // converting apr to two decimal place in table
  convertToTwoDecimalPlaces(apr: any) {
    if (!apr) {
      return 'N/A';
    } else {
      apr = apr.toFixed(2);
      return apr;
    }
  }

  // change program change data from false/true to yes/no
  programChangeDataFormat(data: any) {
    let result;

    if (data === false) {
      result = 'No';
    }
    if (data === true) {
      result = 'Yes';
    }
    return result;
  }

  exportData() {
    if (isPlatformBrowser(this.platformId)) {
      const data: { [key: string]: any }[] = [];
      for (const atRiskStudent of this.atRiskStudentDataSource.filteredData) {
        for (const comment of atRiskStudent.risk_comments) {
          data.push({
            uwf_id: atRiskStudent.uwf_id,
            tier: atRiskStudent.tier,
            demo_time_frame: atRiskStudent.demo_time_frame,
            gender: atRiskStudent.gender,
            cohort: atRiskStudent.cohort,
            classification: atRiskStudent.classification,
            college: atRiskStudent.college,
            department: atRiskStudent.department,
            program_cip: atRiskStudent.program_cip,
            program_cip_desc: atRiskStudent.program_cip_desc,
            changed_program: atRiskStudent.changed_program,
            APR_1st_term_prob: atRiskStudent.prob_apr_1st_term,
            APR_precollege_prob: atRiskStudent.prob_apr_precollege,
            prob_drop: atRiskStudent.prob_drop,
            prob_stay: atRiskStudent.prob_stay,
            uwf_gpa: atRiskStudent.uwf_gpa,
            comment: comment.comment,
            categories: comment.categories,
            made_by: comment.made_by,
          });
        }
      }

      const csvData = this._papa.unparse(data);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const pom = document.createElement('a');
      pom.href = url;
      pom.setAttribute('download', 'at-risk-students.csv');
      pom.click();
    }
  }

  filterChange(event: MatSelectChange, filter: FilterSelect) {
    this.filterDictionary.set(filter.columnProp, event.value);
    const jsonString = JSON.stringify(
      Array.from(this.filterDictionary.entries()),
    );
    this.atRiskStudentDataSource.filter = jsonString;
  }

  createFilter() {
    const filterFunction = function (record: any, filter: string): boolean {
      const map = new Map(JSON.parse(filter));
      let isMatch = false;
      for (const [key, value] of map) {
        if (
          (value as string[]).includes('All') ||
          (value as string[]).length === 0
        ) {
          isMatch = true;
        } else {
          isMatch = (value as number[]).includes(
            record[key as keyof Partial<AtRiskStudentReturn>],
          );
          if (!isMatch) return false;
        }
      }
      return isMatch;
    };
    return filterFunction;
  }

  resetFilters() {
    this.filterSelectObj.forEach((value, _key) => {
      value.defaultValue = ['All'];
    });
    this.atRiskStudentDataSource.filter = '';
  }
}
